import {
  displayFlowUnit,
  DisplayUnit,
  FlowTimeUnits,
  MeasurementUnit,
  Sensor,
} from '@thingslog/repositories';

/**
 * Static resolver for turning raw measurement units into their human-readable
 * display strings. Prefer these over indexing `DisplayUnit` / `displayFlowUnit`
 * directly so the lookup lives in one place.
 */
export class DisplayUnits {
  /** Human-readable display unit for a plain measurement unit (e.g. `CUBIC_METER` -> `m³`). */
  public static getDisplayUnit(unit: MeasurementUnit): string {
    return DisplayUnit[unit];
  }

  /** Human-readable flow unit for a measurement unit over a time unit (e.g. `m³` + `HOURS` -> `m³/h`). */
  public static getDisplayFlowUnit(flowUnit: MeasurementUnit, flowTimeUnit: FlowTimeUnits): string {
    return displayFlowUnit[flowUnit][flowTimeUnit];
  }
}

export const SensorUtils = ({ sensor }: SensorUtilsOptions): SensorUtils => {
  const getSensorType = (): 'digital' | 'analog' | 'onoff' => {
    switch (sensor['@type']) {
      // Analog sensors
      case 'barometric_pressure_sensor':
      case 'dew_point_sensor':
      case 'humidity_sensor':
      case 'level_sensor':
      case 'load_cells':
      case 'modbus_generic_analog_sensor':
      case 'particulate_matter_sensor':
      case 'pressure_sensor':
      case 'soil_moisture_sensor':
      case 'soil_potential_sensor':
      case 'soil_temperature_sensor':
      case 'temperature_sensor':
      case 'weight_meter':
      case 'weight_sensor':
      case 'wind_speed_sensor':
      case 'tasmota_generic_analog_sensor':
      case 'generic_virtual_sensor':
      case 'gnss_sensor':
      case 'leaf_wetness_sensor':
      case 'co_sensor':
      case 'no2_sensor':
      case 'so2_sensor':
      case 'generic_analog_sensor':
        return 'analog';

      // Digital sensors
      case 'gas_flow_meter':
      case 'geiger_mueller_counter':
      case 'heat_meter':
      case 'modbus_generic_digital_sensor':
      case 'power_meter':
      case 'wind_direction_sensor':
      case 'rain_sensor':
      case 'water_meter':
      case 'tasmota_generic_digital_sensor':
      case 'generic_digital_sensor':
        return 'digital';

      // OnOff sensors
      case 'on_off_sensor':
      case 'on_off_input_sensor':
      case 'on_off_output_sensor':
      case 'on_off_auto_output_sensor':
      case 'modbus_generic_on_off_output_sensor':
      case 'modbus_generic_on_off_input_sensor':
      case 'tasmota_relay_sensor':
        return 'onoff';

      default:
        return 'digital';
    }
  };
  const isAnalogSensor = (): boolean => {
    return getSensorType() === 'analog';
  };

  const isDigitalSensor = (): boolean => {
    return getSensorType() === 'digital';
  };

  const isOnOffSensor = (): boolean => {
    return getSensorType() === 'onoff';
  };

  const getSensorUnit = (): string => {
    try {
      let units: string;
      const isGenericDigitalSensor = isDigitalSensor();
      const isGenericAnalogSensor = isAnalogSensor();
      const flowTimeUnits = sensor.flowUnit?.timeUnits;
      const flowUnits = sensor.flowUnit?.units;
      const sensorUnits = sensor.units;

      if (
        isGenericDigitalSensor &&
        flowTimeUnits !== null &&
        flowTimeUnits !== undefined &&
        flowUnits !== null &&
        flowUnits !== undefined
      ) {
        units = DisplayUnits.getDisplayFlowUnit(flowUnits, flowTimeUnits);
      } else if (isGenericAnalogSensor || isGenericDigitalSensor) {
        units = DisplayUnits.getDisplayUnit(sensorUnits);
      } else {
        units = DisplayUnit.NO_UNITS;
      }

      return units;
    } catch {
      return DisplayUnit.NO_UNITS;
    }
  };

  const getFlowUnitDirectly = (): string | null => {
    try {
      let units: string | null = null;
      const flowTimeUnits = sensor.flowUnit?.timeUnits;
      const flowUnits = sensor.flowUnit?.units;

      if (
        flowTimeUnits !== null &&
        flowTimeUnits !== undefined &&
        flowUnits !== null &&
        flowUnits !== undefined
      ) {
        units = DisplayUnits.getDisplayFlowUnit(flowUnits, flowTimeUnits);
      }
      return units;
    } catch {
      return null;
    }
  };

  const getDefaultUnit = (): string => {
    return DisplayUnits.getDisplayUnit(sensor.units);
  };

  return {
    getSensorType,
    isAnalogSensor,
    isDigitalSensor,
    isOnOffSensor,
    getSensorUnit,
    getFlowUnitDirectly,
    getDefaultUnit,
  };
};

export interface SensorUtils {
  getSensorType: () => 'digital' | 'analog' | 'onoff';
  isAnalogSensor: () => boolean;
  isDigitalSensor: () => boolean;
  isOnOffSensor: () => boolean;
  getSensorUnit: () => string;
  getFlowUnitDirectly: () => string | null;
  getDefaultUnit: () => string;
}

interface SensorUtilsOptions {
  sensor: Sensor;
}
